import Alert from '@/components/alert/alert-component';

/**
 * Get data in your storage
 * @param {string} key - What the name of the local storage item will be
 * @returns {object} - Returns a the required values
 */
export function getFromStorage(key) {
  if (!key) {
    return null;
  }
  try {
    const valueStr = localStorage.getItem(key);
    if (valueStr) {
      return valueStr;
    }
    return null;
  } catch (err) {
    return null;
  }
}

/**
 * Set data in your storage
 * Convert to sentence case
 * @param {string} string - String to put into sentence case
 * @returns {string} - Returns a the required values
 */
export function toSentenceCase(string) {
  // Split the string into an array of sentences
  const sentences = string?.split(/(?<=[.!?])\s+/);

  // Capitalize the first letter of each sentence and convert the rest to lowercase
  const sentenceCaseSentences = sentences?.map((sentence) => {
    const trimmedSentence = sentence.trim();

    // Check if the sentence is a concatenated word
    if (/[a-z][A-Z]/.test(trimmedSentence)) {
      // If it contains a transition from lowercase to uppercase (camelCase or PascalCase),
      // insert a space between them and then capitalize each word
      const words = trimmedSentence.split(/(?<=[a-z])(?=[A-Z])/);
      const spacedWords = words.map(
        (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
      );
      return spacedWords?.join(' ');
    }
    // If no transitions, capitalize the first letter and convert the rest to lowercase
    return (
      trimmedSentence.charAt(0).toUpperCase() +
      trimmedSentence.slice(1).toLowerCase()
    );
  });

  // Join the sentences back into a string
  const sentenceCaseString = sentenceCaseSentences?.join(' ');

  return sentenceCaseString;
}

/**
 *Set data in your storage
 * @param {string} key - What the name of the local storage item will be
 * @param {object} obj - The object to be stored
 * @returns {boolean} - Returns a boolean
 */
export function setInStorage(key, obj) {
  if (!key) {
    return false;
  }
  try {
    localStorage.setItem(key, JSON.stringify(obj));
    return true;
  } catch (err) {
    return false;
  }
}

/**
 * Get error message from nested object for React Hook Form
 * @param {string} fieldName - The name of the field from which to get the error message
 * @param {obj} errors - The errors object from React Hook Form
 * @returns {string} - Returns the error message
 * @example
 * const errorMessage = getNestedErrorMessage(name);
 */
export const getNestedErrorMessage = (fieldName, errors) => {
  let nestedErrors = errors;
  const fieldNames = fieldName.split('.');

  for (let i = 0; i < fieldNames.length; i += 1) {
    const errorName = fieldNames[i];
    nestedErrors = nestedErrors[errorName];
    if (!nestedErrors) return null;
  }

  return nestedErrors.message;
};

/**
 * Converts file object to Base64 string representation
 * @param {object} file - File object to be converted
 * @returns {Promise} - Returns a promise
 */
export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

/**
 * Removes all undefined properties from an object recursively.
 * This will create a New object. This object includes all properties from the original object that had defined values, including defined properties in nested objects
 * @param {object} obj - The object to be cleaned
 * @returns {object} - Returns a clean object with no undefined properties
 */
export const removeUndefinedProperties = (obj) =>
  Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== undefined) {
      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        acc[key] = removeUndefinedProperties(value);
      } else {
        acc[key] = value;
      }
    }
    return acc;
  }, {});

/**
 * Sends image to S3 bucket
 * @param {object} payload - The object to be stored
 * @property {string} payload.path - The path of the image in the S3 bucket
 * @property {string} payload.fileName - The name of the image
 * @property {string} payload.file - The base64 representation of the image
 * @returns {void} - Returns nothing
 */
export const sendImageToS3 = (payload) => {
  fetch(`https://s3.amazonaws.com/ettx-public-assets/${payload.path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
    .then(() => {
      Alert({
        icon: 'success',
        text: 'Your image has been uploaded successfully',
      });
    })
    .catch(() => {
      Alert({
        icon: 'error',
        text: 'There was a problem uploading your image',
      });
    });
};

/*
 * Combine all a users roles into a single permissions object.
 * A permission is that is allowed overrides all other permissions
 * that are not allowed on other roles.
 * @param {object} permissions the permissions
 * @returns {object}
 */
const combinePermissions = (permissions = {}) => {
  const permissionsRet = {};
  const actions = ['create', 'read', 'update', 'delete'];
  Object.keys(permissions).forEach((category) => {
    if (!permissionsRet[category]) {
      permissionsRet[category] = permissions[category];
    } else {
      actions.forEach((action) => {
        permissionsRet[category][action] =
          !!permissions[category][action] || !!permissions[category][action];
      });
    }
  });
  return permissionsRet;
};

/**
 * Check if the user has multiple permissions to access a resource.
 * Check if a user is allowed based on his roles to perform an action.
 * @param {*} roles - Role objects that are assigned to a user
 * @param {string[]} permissions - The permissions you want to check.
 * In the format "category.action".
 * @param {boolean} satisfyAll - Should all the permissions be satisfied, or
 * should only a single permission be satisfied to return true
 * @example
 * const roles = [
 * {title: 'Admin', permissions: { user: { create: true, read: true, update: true, delete: true}}},
 * { title: 'Admin2', permissions: { vehicles: { read: true }}},
 * ];
 * checkPermission(roles, ['user.all', 'vehicle.read']); // Returns true
 * checkPermissions(roles, ['vehicle.create', 'vehicle.read']); // Returns false
 * checkPermissions(roles, ['vehicle.create', 'vehicle.read'], false); // Returns true
 * @returns {boolean}
 */
export const checkPermissions = (roles, permissions, satisfyAll = true) => {
  const actions = ['create', 'read', 'update', 'delete'];
  const combPermissions = combinePermissions(roles);
  let perms = permissions;
  if (!Array.isArray(permissions)) {
    perms = [permissions];
  }
  if (!permissions.length) {
    return true;
  }
  return perms[satisfyAll ? 'every' : 'some']((permission) => {
    const [category, action] = permission.split('.');
    if (!combPermissions[category]) {
      return false;
    }
    if (action === 'all') {
      return actions.every(
        (subAction) => !!combPermissions[category][subAction],
      );
    }
    return !!combPermissions[category][action];
  });
};

/**
 * @param  {string} string - string to truncate
 * @returns {string}
 */
export function truncate(string) {
  if (!string) return '';
  return string.length > 30 ? `${string.substring(0, 20)}...` : string;
}

/**
 * @param  {string} string - string to truncate
 * @param {number} value - value to truncate to
 * @returns {string}
 */
export function truncateLongString(string, value = 20) {
  if (!string) return '';
  return string.length > value ? `${string.substring(0, value)}...` : string;
}

export default { getFromStorage, setInStorage };
